import { Upload, Button, Popover, message } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import { t } from 'i18next';
import { RcFile } from 'antd/es/upload';
import { ButtonType } from 'antd/es/button';
import { observer } from 'mobx-react';

import { ALLOWED_IMAGE_UPLOAD_FILES } from '@/comp/Forms/SiteSurveyForm/RoomConfigurationStep/FormComponents/ImageUpload/ImageUpload.consts';
import { isAndroid } from '@/utils/util-functions';
import { useStores } from '@/hooks/use-stores';

import { ImageUploadPopoverStyles } from './ImageUploadButtons.styles';

type Props = {
  compressImage: (file: RcFile, files: RcFile[]) => Promise<string>;
  isLoading: boolean;
  buttonType?: ButtonType;
  buttonText: string;
  isDisabled?: boolean;
  showFileLimitError?: boolean;
  setShowFileLimitError?: (showFileLimitError: boolean) => void;
};

export const ImageUploadButtons: FC<Props> = observer(
  ({
    compressImage,
    isLoading,
    buttonText,
    buttonType = 'primary',
    isDisabled = false,
    showFileLimitError,
    setShowFileLimitError,
  }) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const { imageUploadStore } = useStores();
    const [messageApi, contextHolder] = message.useMessage();

    useEffect(() => {
      if (showFileLimitError) {
        messageApi.error(t('uploadImageFileLimitErrorMessage', { limit: imageUploadStore.maxFiles }));
        setShowFileLimitError(false);
      }
    }, [showFileLimitError]);

    const handlePopoverCompressImage = async (file: RcFile, files: RcFile[]) => {
      setIsPopoverOpen(false);
      await compressImage(file, files);
    };

    const isAndroidDevice = useMemo(() => isAndroid(), []);

    return isAndroidDevice ? (
      <>
        <ImageUploadPopoverStyles />
        <Popover
          open={isPopoverOpen}
          onOpenChange={setIsPopoverOpen}
          trigger="click"
          rootClassName="image-upload-popover"
          content={
            <div className="popover-content">
              <h3 className="popover-title">{t('uploadImagePopoverTitle')}</h3>
              <Upload
                className="mobile-image-select-upload mobile-image-select-upload-camera"
                capture
                accept={ALLOWED_IMAGE_UPLOAD_FILES}
                showUploadList={false}
                beforeUpload={handlePopoverCompressImage}
              >
                <Button block type="link" disabled={imageUploadStore.noEditPermission}>
                  {t('uploadImageTakePictureButtonText')}
                </Button>
              </Upload>
              <Upload
                className="mobile-image-select-upload mobile-image-select-upload-gallery"
                accept={ALLOWED_IMAGE_UPLOAD_FILES}
                showUploadList={false}
                beforeUpload={handlePopoverCompressImage}
                multiple
              >
                <Button block type="link" disabled={imageUploadStore.noEditPermission}>
                  {t('uploadImageFromGalleryButtonText')}
                </Button>
              </Upload>
            </div>
          }
        >
          <Button
            size="large"
            type={buttonType}
            disabled={imageUploadStore.noEditPermission || isLoading || isDisabled}
            block
            loading={isLoading}
          >
            {t(buttonText)}
          </Button>
        </Popover>
      </>
    ) : (
      <>
        {contextHolder}
        <Upload
          className="mobile-image-select-upload"
          accept={ALLOWED_IMAGE_UPLOAD_FILES}
          showUploadList={false}
          beforeUpload={compressImage}
          multiple
        >
          <Button
            size="large"
            type={buttonType}
            disabled={imageUploadStore.noEditPermission || isLoading || isDisabled}
            block
            loading={isLoading}
          >
            {t(buttonText)}
          </Button>
        </Upload>
      </>
    );
  },
);
